import React from 'react'

import {
    NoticeBox,
    NoticeBoxColors,
    NoticeBoxVariants,
    Spinner,
    StAlertIcon,
    theme,
    VsExclamationIcon,
} from '@octane/spark'
import HtmlFormattedText from 'components/HtmlFormattedText'
import useApplicationComments from 'hooks/useApplicationComments'
import ApplicationStatus from 'state/enums/ApplicationStatus'

import {
    BoxHeader,
    CommentItem,
    CommentsContainer,
    CommentList,
    CommentsSpinnerWrapper,
} from './DecisionResultComments.styled'

export default function DecisionResultComments({
    applicationId,
    status,
}: {
    readonly applicationId: number
    readonly status: string
}): JSX.Element {
    const { applicationComments, loading, error } =
        useApplicationComments(applicationId)

    const isDecline = status === ApplicationStatus.DECLINE

    const renderCommentsList = () => {
        return (
            <CommentList>
                {applicationComments.map((comment) => (
                    <CommentItem key={comment.id} isDecline={isDecline}>
                        {isDecline ? (
                            <StAlertIcon
                                color={theme.colors.alert.error}
                                width={20}
                                height={20}
                            />
                        ) : (
                            <StAlertIcon
                                color={theme.colors.alert.note}
                                width={20}
                                height={20}
                            />
                        )}
                        <HtmlFormattedText text={comment.text} />
                    </CommentItem>
                ))}
            </CommentList>
        )
    }

    const renderComments = () => {
        if (loading) {
            return (
                <CommentsSpinnerWrapper data-testid="DecisionResultComments-spinner">
                    <Spinner size={24} />
                </CommentsSpinnerWrapper>
            )
        }

        if (error) {
            return (
                <NoticeBox
                    // eslint-disable-next-line max-len
                    content="An error occurred when loading the comments. Refresh the page to try again or contact support."
                    heading=""
                    icon={VsExclamationIcon}
                    color={NoticeBoxColors.ERROR}
                    variant={NoticeBoxVariants.BOX_HIGHLIGHTED}
                />
            )
        }

        if (applicationComments.length > 0) {
            return (
                <CommentsContainer isDecline={isDecline}>
                    <BoxHeader>
                        This application was not approved due to the following
                        reasons:
                    </BoxHeader>
                    {renderCommentsList()}
                </CommentsContainer>
            )
        }

        return null
    }

    return <div>{renderComments()}</div>
}
